import { Col, Form, Row } from 'antd';
import { RuleObject } from 'antd/es/form';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import { DfpTable } from 'components/molecules/Dokumenty/DfpTable';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import {
    EDuvodZnizeni,
    EPredaniMajitaelu,
    IPrikazSnizeni,
    PrikazSnizeniGet,
    PrikazSnizeniPut
} from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import { isIsinDlt } from '../NovyDokument';
import { getDataIsins } from './Zruseni';

interface IPrikazZnizeni {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const PrikazZnizeni: React.FC<IPrikazZnizeni> = ({ nextStep, zadostID, prikazyData }) => {
    const [form] = Form.useForm();
    const { cisMeny, dokumenty } = useContext(Context);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [data, setData] = useState<IPrikazSnizeni>();

    const saveData = (vals: any) => {
        const dataToSend = {
            ...(vals as any),
            datum: vals.datum.format('YYYY-MM-DD')
        };
        delete dataToSend.idmbSpolecnost;
        delete dataToSend.dicdphSpolecnost;
        delete dataToSend.nazevSpolecnost;
        delete dataToSend.leiSpolecnost;
        setData(dataToSend);
        return PrikazSnizeniPut(zadostID, dataToSend);
    };

    const [DfpTableRender, DfpUcetRender, SouborRender, displayMode] = DfpTable(
        zadostID,
        () => {
            saveData(form.getFieldsValue());
            setData(pv => {
                return {
                    ...pv,
                    ...form.getFieldsValue(),
                    datum: form.getFieldsValue().datum.format('YYYY-MM-DD')
                };
            });
        },
        form,
        false,
        'znizeni'
    );
    // const [fileRespData, setFileRespData] = useState<INacteni>();
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );

    const [selectedIsin, setSelectIsin] = useState<string>();
    const [isDLT, setIsDLT] = useState<boolean>();
    useEffect(() => {
        if (selectedIsin) {
            setIsDLT(isIsinDlt(selectedIsin, dokumenty!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    useEffect(() => {
        setLoading(true);
        PrikazSnizeniGet(zadostID)
            .then(resp => {
                setSelectIsin(resp.isin);
                setData(resp);
                form.setFieldsValue({
                    typsezmaj: resp.typsezmaj,
                    celkhodnotap: resp.navakcie && resp.pocetcpp ? resp.navakcie * resp.pocetcpp : undefined
                });
            })
            .catch(() => setData({} as IPrikazSnizeni))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data &&
                prikazyData &&
                (displayMode === 'soubor' ? (
                    SouborRender()
                ) : displayMode === 'ucet' ? (
                    DfpUcetRender()
                ) : (
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            setLoading(true);
                            saveData(values)
                                .then(() => nextStep())
                                .finally(() => setLoading(false));
                        }}
                        preserve={true}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            data={dataIsins}
                            isFormItem={true}
                            onChange={val => setSelectIsin(val)}
                        />
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            //   disabledDate={current => current < dayjs()}
                            label="PRIKAZ_PREVOD_DATUM"
                        />
                        <SelectComponent
                            name="typduvod"
                            labelParagraph="PRIKAZ_ZNIZENI_DUVOD"
                            isFormItem={true}
                            data={[
                                {
                                    value: EDuvodZnizeni.ZRUSAKCZK,
                                    labelcz: 'PRIKAZ_ZNIZENI_ZRUSAKCZK'
                                },
                                {
                                    value: EDuvodZnizeni.ZRUSAKINV,
                                    labelcz: 'PRIKAZ_ZNIZENI_ZRUSAKINV'
                                },
                                {
                                    value: EDuvodZnizeni.SNIZDLUH,
                                    labelcz: 'PRIKAZ_ZNIZENI_SNIZDLUH'
                                },
                                {
                                    value: EDuvodZnizeni.SNIZINV,
                                    labelcz: 'PRIKAZ_ZNIZENI_SNIZINV'
                                },
                                {
                                    value: EDuvodZnizeni.SNIZWAR,
                                    labelcz: 'PRIKAZ_ZNIZENI_SNIZWAR'
                                },
                                {
                                    value: EDuvodZnizeni.SNIZJINY,
                                    labelcz: 'PRIKAZ_ZNIZENI_SNIZJINY'
                                }
                            ]}
                            defaultValue={data?.typduvod}
                        />
                        <Title level={4} dividerTitle={true} text="PRIKAZ_ZNIZENI_UDAJECP" />
                        <InputNumberComponent
                            name="navakcie"
                            label="PRIKAZ_ZNIZENI_1CP_HODNOTA"
                            initialValue={data?.navakcie}
                            onChangeCustom={val => {
                                form.setFieldsValue({
                                    celkhodnotap:
                                        form.getFieldValue('pocetcpp') && val
                                            ? Number(form.getFieldValue('pocetcpp')) * Number(val)
                                            : undefined,
                                    celkhodnota:
                                        form.getFieldValue('pocetcpn') && val
                                            ? Number(form.getFieldValue('pocetcpn')) * Number(val)
                                            : undefined
                                });
                                rerender(pv => !pv);
                            }}
                            precision={4}
                        />
                        <SelectComponent
                            name="mena"
                            labelParagraph="PRIKAZ_ZNIZENI_NAV"
                            isFormItem={true}
                            data={cisMeny}
                            onChange={() => rerender(pv => !pv)}
                            defaultValue={data?.mena}
                        />
                        <InputNumberComponent
                            name="pocetcpp"
                            label="PRIKAZ_ZNIZENI_POCET_PRED"
                            initialValue={data?.pocetcpp}
                            precision={0}
                            onChangeCustom={val => {
                                form.setFieldsValue({
                                    celkhodnotap:
                                        form.getFieldValue('navakcie') && val
                                            ? Number(form.getFieldValue('navakcie')) * Number(val)
                                            : undefined,
                                    poccpsniz:
                                        form.getFieldValue('pocetcpn') && val
                                            ? Number(val) - Number(form.getFieldValue('pocetcpn'))
                                            : undefined
                                });
                                rerender(pv => !pv);
                            }}
                        />
                        <InputNumberComponent
                            name="celkhodnotap"
                            label="PRIKAZ_JMENOVITA_UDAJE_PRED_CELKOVA_HODNOTA"
                            initialValue={data?.celkhodnotap}
                            disabled={true}
                            notRequired={true}
                        />
                        <Title level={4} dividerTitle={true} text="PRIKAZ_ZNIZENI_ZMENA_TITLE" />
                        <InputNumberComponent
                            name="pocetcpn"
                            label="PRIKAZ_ZNIZENI_NOVY_POCET"
                            initialValue={data?.pocetcpn}
                            precision={0}
                            onChangeCustom={val => {
                                form.setFieldsValue({
                                    celkhodnota:
                                        form.getFieldValue('navakcie') && val
                                            ? Number(form.getFieldValue('navakcie')) * Number(val)
                                            : undefined,
                                    poccpsniz:
                                        form.getFieldValue('pocetcpp') && val
                                            ? Number(form.getFieldValue('pocetcpp')) - Number(val)
                                            : undefined
                                });

                                rerender(pv => !pv);
                            }}
                            rules={[
                                () => ({
                                    validator(_rule: RuleObject, value: string) {
                                        if (
                                            !value ||
                                            !form.getFieldValue('pocetcpp') ||
                                            Number(value) < Number(form.getFieldValue('pocetcpp'))
                                        )
                                            return Promise.resolve();
                                        return Promise.reject(<TranslateText>POCETCP_ZNIZENI_ERR</TranslateText>);
                                    }
                                })
                            ]}
                        />
                        <InputNumberComponent
                            name="poccpsniz"
                            label="PRIKAZ_ZNIZENI_NOVY_SNIZENI"
                            initialValue={data?.poccpsniz}
                            precision={0}
                            notRequired={true}
                            disabled={true}
                        />
                        <InputNumberComponent
                            name="celkhodnota"
                            label="PRIKAZ_ZNIZENI_NOVY_HODNOTA"
                            initialValue={data?.celkhodnota}
                            disabled={true}
                            notRequired={true}
                        />
                        <SelectComponent
                            name="typsezmaj"
                            labelParagraph="PRIKAZ_ZNIZENI_SPUSOB_PREDANI"
                            isFormItem={true}
                            data={[
                                {
                                    value: EPredaniMajitaelu.CDCP,
                                    labelcz: 'PRIKAZ_ZNIZENI_SPUSOB_PREDANI_CDCP'
                                },
                                {
                                    value: EPredaniMajitaelu.MD,
                                    labelcz: 'PRIKAZ_ZNIZENI_SPUSOB_PREDANI_MD'
                                },
                                {
                                    value: EPredaniMajitaelu.MDADMIN,
                                    labelcz: 'PRIKAZ_ZNIZENI_SPUSOB_PREDANI_MDADMIN'
                                }
                            ]}
                            onChange={() => rerender(pv => !pv)}
                            defaultValue={data?.typsezmaj}
                        />
                        {form.getFieldValue('typsezmaj') === EPredaniMajitaelu.CDCP && (
                            <>
                                <Paragraph
                                    text="PRIKAZ_ZNIZENI_DFP_TEXT"
                                    style={{
                                        display: 'block'
                                    }}
                                />
                                {DfpTableRender()}
                            </>
                        )}
                        <PrilohyTable zadostID={zadostID} required={true} />
                        <Title level={5} text="PRIKAZ_ZNIZENI_PODMINKY_TITLE" />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_ZNIZENI_PODMINKY_DLT_TEXT' : 'PRIKAZ_ZNIZENI_PODMINKY_TEXT'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_ZNIZENI_PODMINKY_DLT_TEXT1' : 'PRIKAZ_ZNIZENI_PODMINKY_TEXT1'}
                            style={{
                                display: 'block',
                                marginTop: 10
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_ZNIZENI_PODMINKY_DLT_TEXT11' : 'PRIKAZ_ZNIZENI_PODMINKY_TEXT11'}
                            style={{
                                display: 'block',
                                marginLeft: 24
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_ZNIZENI_PODMINKY_DLT_TEXT12' : 'PRIKAZ_ZNIZENI_PODMINKY_TEXT12'}
                            style={{
                                display: 'block',
                                marginLeft: 24
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_ZNIZENI_PODMINKY_DLT_TEXT13' : 'PRIKAZ_ZNIZENI_PODMINKY_TEXT13'}
                            style={{
                                display: 'block',
                                marginLeft: 24
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_ZNIZENI_PODMINKY_DLT_TEXT2' : 'PRIKAZ_ZNIZENI_PODMINKY_TEXT2'}
                            style={{
                                display: 'block',
                                marginTop: 10
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_ZNIZENI_PODMINKY_DLT_TEXT21' : 'PRIKAZ_ZNIZENI_PODMINKY_TEXT21'}
                            style={{
                                display: 'block',
                                marginLeft: 24
                            }}
                        />
                        {!isDLT && (
                            <Paragraph
                                text={'PRIKAZ_ZNIZENI_PODMINKY_TEXT22'}
                                style={{
                                    display: 'block',
                                    marginLeft: 24
                                }}
                            />
                        )}
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_ZNIZENI_PODMINKY_DLT_TEXT3' : 'PRIKAZ_ZNIZENI_PODMINKY_TEXT3'}
                            style={{
                                display: 'block',
                                marginTop: 10
                            }}
                        />
                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                ))}
        </LoadingWrapper>
    );
};

export default PrikazZnizeni;
